export interface RouteConfig {
  path: string;
  label: string; // Label for Sidebar
  featureName: string; // Name for Teaser Page
  minimumRequiredRole?: string; // Optional: Minimum role needed for access
  // Add other relevant info if needed, e.g., Icon for Sidebar
}

// Define your subscription roles
export const ROLES = {
  TRIAL: 'trial-subscriber',
  BASIC: 'basic-subscriber',
  STANDARD: 'standard-subscriber',
  ELITE: 'elite-subscriber',
  ADMIN: 'admin-user' // Updated to match Auth0 role name, was 'admin-subscriber'
};

// Define the hierarchy of roles (lowest to highest)
export const ROLE_HIERARCHY = [ROLES.TRIAL, ROLES.BASIC, ROLES.STANDARD, ROLES.ELITE, ROLES.ADMIN];

// Define the configuration based on the new requirements
export const routeConfig: RouteConfig[] = [  {
    path: '/dashboard',
    label: 'Dashboard',
    featureName: 'Dashboard',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to trial and above
  },
  {
    path: '/admin', // New Admin Route
    label: 'Admin Panel', // Sidebar Label for Admin
    featureName: 'Admin Panel', // Teaser Page Feature Name for Admin
    minimumRequiredRole: ROLES.ADMIN, // Accessible only to Admin users
  },
  {
    path: '/real-time-diagnostic', // Real-Time Diagnostic Route
    label: 'RT Diagnostic', // Sidebar Label for Diagnostic
    featureName: 'Real-Time Architecture Diagnostic', // Teaser Page Feature Name
    minimumRequiredRole: ROLES.ADMIN, // Accessible only to Admin users (for testing)
  },
  {
    path: '/lifecycle-testing', // User Lifecycle Testing Route
    label: 'Lifecycle Testing', // Sidebar Label for Lifecycle Testing
    featureName: 'User Lifecycle Testing Dashboard', // Teaser Page Feature Name
    minimumRequiredRole: ROLES.ADMIN, // Accessible only to Admin users (for testing)
  },
  {
    path: '/portfolio-optimization', // New Route
    label: 'Optimization',          // Sidebar Label
    featureName: 'Portfolio Optimization', // Teaser Page Feature Name
    minimumRequiredRole: ROLES.ELITE,    // Example: Elite and above
  },
  {
    path: '/portfolio-command',
    label: 'Command Center',
    featureName: 'Portfolio Command Center',
    minimumRequiredRole: ROLES.ELITE, // Standard and above
  },
  {
    path: '/portfolio-command-v2',
    label: 'Command Center v2', 
    featureName: 'Portfolio Command Center (Redesigned)',
    minimumRequiredRole: ROLES.ELITE, // Same access level as original
  },  {
    path: '/live-trades',
    label: 'Live Trades',
    featureName: 'Live Trades',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to trial and above
  },
  {
    path: '/closed-trades',
    label: 'Closed Trades',
    featureName: 'Closed Trades',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to all authenticated subscribers
  },
  {
    path: '/strategy-details',
    label: 'Strategy Details',
    featureName: 'Strategy Details',
    minimumRequiredRole: ROLES.STANDARD, // Standard and above
  },
  {
    path: '/forecast',
    label: 'Forecast',
    featureName: 'Portfolio Forecast',
    minimumRequiredRole: ROLES.STANDARD, // Standard and above
  },
  {
    path: '/bot-maintenance',
    label: 'Bot Maintenance',
    featureName: 'Bot Maintenance',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to all authenticated subscribers
  },
  {
    path: '/faq',
    label: 'Help',
    featureName: 'Help/FAQ',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to all authenticated subscribers
  },  {
    path: '/settings',
    label: 'Settings',
    featureName: 'Account Settings',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to all authenticated subscribers
  },
  {
    path: '/email-preferences',
    label: 'Email Preferences',
    featureName: 'Email Preferences',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to all authenticated subscribers
  },
  {
    path: '/privacy',
    label: 'Privacy & Data',
    featureName: 'Privacy & Data Management',
    minimumRequiredRole: ROLES.TRIAL, // Accessible to all authenticated subscribers
  },
  // Add other non-sidebar routes like /checkout, /subscription-success if needed
  // { path: '/checkout', label: 'Checkout', featureName: 'Checkout' }, // Example
  // { path: '/subscription-success', label: 'Success', featureName: 'Subscription Success' }, // Example
];

// Helper function to find config by path (can be enhanced for dynamic paths)
export const getRouteConfigByPath = (path: string): RouteConfig | undefined => {
  // Simple match for now, might need refinement for paths like /live-trades/chart/...
  return routeConfig.find(route => path.startsWith(route.path));
};

/**
 * Helper function to check if a user's role meets the minimum requirement for a route.
 * @param userRole The role of the current user.
 * @param minimumRequiredRole The minimum role required for the route.
 * @returns True if the user meets the requirement, false otherwise.
 */
export const hasRequiredRole = (userRole: string | undefined | null, minimumRequiredRole: string | undefined): boolean => {
  if (!minimumRequiredRole) {
    return true; // No specific role required, accessible to all authenticated users
  }
  if (!userRole) {
    return false; // User has no role, cannot access restricted route
  }

  const userLevel = ROLE_HIERARCHY.indexOf(userRole);
  const requiredLevel = ROLE_HIERARCHY.indexOf(minimumRequiredRole);

  if (userLevel === -1 || requiredLevel === -1) {
    console.warn(`Role check issue: User Role ('${userRole}') or Required Role ('${minimumRequiredRole}') not found in hierarchy.`);
    return false; // Role not found in hierarchy
  }

  return userLevel >= requiredLevel; // User's level must be >= required level
};
